import { useEffect } from "react";
import "@/index.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProfileProvider } from "@/context/ProfileContext";
import { CurrencyProvider } from "@/context/CurrencyContext";
import { ProfileGate } from "@/components/ProfileGate";
import { SplashScreen } from "@/components/SplashScreen";
import { InstallPrompt } from "@/components/InstallPrompt";
import { ScrollToTop } from "@/components/ScrollToTop";
import { MaintenanceGate } from "@/components/MaintenanceGate";
import { BackButton } from "@/components/BackButton";
import { AmbientPlayer } from "@/components/AmbientPlayer";
import { ScreenSaver } from "@/components/ScreenSaver";
import { VisitTracker } from "@/components/VisitTracker";
import Landing from "@/pages/Landing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Merci from "@/pages/Merci";
import Auth from "@/pages/Auth";
import Atelier from "@/pages/Atelier";
import Profil from "@/pages/Profil";
import Abonnement from "@/pages/Abonnement";
import Tarifs from "@/pages/Tarifs";
import Manifeste from "@/pages/Manifeste";
import Legal from "@/pages/Legal";
import Echo from "@/pages/Echo";
import EchoTopic from "@/pages/EchoTopic";
import PodcastPublic from "@/pages/PodcastPublic";
import DuoRoom from "@/pages/DuoRoom";
import CercleRoom from "@/pages/CercleRoom";
import AdminDashboard from "@/pages/AdminDashboard";
import Faq from "@/pages/Faq";
import EspaceEnfant from "@/pages/EspaceEnfant";
import Report from "@/pages/Report";
import Radio from "@/pages/Radio";
import RadioBirth from "@/pages/RadioBirth";
import Essais from "@/pages/Essais";
import Essai from "@/pages/Essai";
import { RadioProvider } from "@/lib/RadioContext";
import { RadioBubble } from "@/components/RadioBubble";
import CarteLueurs from "@/pages/CarteLueurs";
import QuiParleIA from "@/pages/QuiParleIA";
import CheminDeSoie from "@/pages/CheminDeSoie";
import EcoleCocoon from "@/pages/EcoleCocoon";
import ExplorerPublic from "@/pages/ExplorerPublic";
import Invitation from "@/pages/Invitation";
import Philosophie from "@/pages/Philosophie";
import AcademieIA from "@/pages/AcademieIA";
import Univers from "@/pages/Univers";
import Spirale from "@/pages/Spirale";
import Chambre from "@/pages/Chambre";
import Memoire from "@/pages/Memoire";

function RefCapture() {
  const location = useLocation();
  useEffect(() => {
    const ref = new URLSearchParams(location.search).get("ref");
    if (ref && ref.trim()) localStorage.setItem("coccon_ref", ref.trim());
  }, [location.search]);
  return null;
}

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) {
    return (
      <div className="min-h-screen starfield flex items-center justify-center">
        <p className="font-display italic text-2xl text-foreground/50">L'Élan s'éveille…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/entrer" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking || user === null) {
    return (
      <div className="min-h-screen starfield flex items-center justify-center">
        <p className="font-display italic text-2xl text-foreground/50">L'Élan s'éveille…</p>
      </div>
    );
  }
  if (!user) return <Navigate to="/entrer" replace />;
  if (user.role !== "admin") return <Navigate to="/atelier" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/univers/:slug" element={<Univers />} />
      <Route path="/spirale" element={<Spirale />} />
      <Route path="/chambre" element={<Chambre />} />
      <Route path="/memoire" element={<Memoire />} />
      <Route path="/tarifs" element={<Tarifs />} />
      <Route path="/manifeste" element={<Manifeste />} />
      <Route path="/philosophie" element={<Philosophie />} />
      <Route path="/academie-ia" element={<AcademieIA />} />
      <Route path="/echo" element={<Echo />} />
      <Route path="/echo/:id" element={<EchoTopic />} />
      <Route path="/podcast/:id" element={<PodcastPublic />} />
      <Route path="/duo/:id" element={<DuoRoom />} />
      <Route path="/cercle/:id" element={<CercleRoom />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/enfants" element={<EspaceEnfant />} />
      <Route path="/signalement" element={<Report />} />
      <Route path="/derive" element={<Navigate to="/radio" replace />} />
      <Route path="/radio" element={<Radio />} />
      <Route path="/radio/naissance" element={<RadioBirth />} />
      <Route path="/essais" element={<Essais />} />
      <Route path="/essais/:slug" element={<Essai />} />
      <Route path="/carte-des-lueurs" element={<CarteLueurs />} />
      <Route path="/qui-parle-aux-ia" element={<QuiParleIA />} />
      <Route path="/chemin-de-soie" element={<CheminDeSoie />} />
      <Route path="/ecole-cocoon" element={<EcoleCocoon />} />
      <Route path="/invitation" element={<Invitation />} />
      <Route path="/explorateur/:handle" element={<ExplorerPublic />} />
      <Route path="/legal/:doc" element={<Legal />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/merci" element={<Merci />} />
      <Route path="/entrer" element={<Auth />} />
      <Route
        path="/atelier"
        element={
          <ProtectedRoute>
            <ProfileGate>
              <Atelier />
            </ProfileGate>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profil"
        element={
          <ProtectedRoute>
            <Profil />
          </ProtectedRoute>
        }
      />
      <Route
        path="/abonnement"
        element={
          <ProtectedRoute>
            <Abonnement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AdminDashboard />
          </AdminRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    document.title = "COCOON — L'Élan";
    const setVVH = () => {
      const h = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
      document.documentElement.style.setProperty("--vvh", `${h}px`);
    };
    setVVH();
    const vv = window.visualViewport;
    window.addEventListener("resize", setVVH);
    window.addEventListener("orientationchange", setVVH);
    if (vv) {
      vv.addEventListener("resize", setVVH);
      vv.addEventListener("scroll", setVVH);
    }
    return () => {
      window.removeEventListener("resize", setVVH);
      window.removeEventListener("orientationchange", setVVH);
      if (vv) {
        vv.removeEventListener("resize", setVVH);
        vv.removeEventListener("scroll", setVVH);
      }
    };
  }, []);
  return (
    <AuthProvider>
      <ProfileProvider>
        <CurrencyProvider>
        <BrowserRouter>
          <ScrollToTop />
          <VisitTracker />
          <RefCapture />
          <SplashScreen />
          <InstallPrompt />
          <MaintenanceGate>
            <RadioProvider>
              <AppRoutes />
              <BackButton />
              <AmbientPlayer />
              <RadioBubble />
              <ScreenSaver />
            </RadioProvider>
          </MaintenanceGate>
          <Toaster
            theme="dark"
            position="bottom-right"
            toastOptions={{
              style: {
                background: "hsl(215 25% 9%)",
                border: "1px solid hsl(40 30% 90% / 0.1)",
                color: "hsl(40 20% 90%)",
              },
            }}
          />
        </BrowserRouter>
        </CurrencyProvider>
      </ProfileProvider>
    </AuthProvider>
  );
}

export default App;
